/**
 * Settings → Shipping & delivery: zones with rates (fixed / carrier-calculated / pickup / local delivery), pickup
 * locations, ship-from address, packages, tracking (17TRACK / AfterShip), labels (Shippo / EasyPost), returns.
 * Prop-less; uses useSite(). API: shipping-zones, shipping-settings, pickup-locations (SPEC §6.3).
 */
import React, { useEffect, useState } from 'react';
import {
    Button, Card, Checkbox, Col, Collapse, Empty, Form, Input, InputNumber, Modal, Popconfirm, Radio, Row, Select, Space, Spin, Switch, Table, Tag, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, EnvironmentOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import CountryFlag from '../../../shared/components/CountryFlag';
import { QUIET } from './api';
import { Hint, listOf, money, toastErr, useLoad, useSite } from './common';
import { CountrySelect, MoneyInput, countryLabel } from './ShopCommon';

const { Text } = Typography;
const UNIT = { g: 1, kg: 1000, lb: 453.59237, oz: 28.349523125 };
const FIXED = ['flat', 'weight', 'price'];
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const kindOf = (type) => (FIXED.includes(type) ? 'fixed' : type);

// ---------------------------------------------------------------- rates / zones

const RateModal = ({ open, rate, settings, onClose, onOk }) => {
    const { t } = useTranslation();
    const { currency, shop } = useSite();
    const unit = shop?.weight_unit || 'kg';
    const [r, setR] = useState(null);
    useEffect(() => {
        if (open) setR({ name: '', type: 'flat', price: 0, min_value: null, max_value: null, free_over: null, eta: '', min_days: null, max_days: null, ...(rate || {}), config: { ...(rate?.config || {}) } });
    }, [open, rate]);
    if (!r) return null;
    const set = (p) => setR((x) => ({ ...x, ...p }));
    const cfg = (p) => setR((x) => ({ ...x, config: { ...x.config, ...p } }));
    const w = (g) => (g === null || g === undefined ? null : +(g / UNIT[unit]).toFixed(3));
    const g = (v) => (v === null || v === undefined ? null : Math.round(v * UNIT[unit]));
    const kind = kindOf(r.type);
    const pickups = listOf(settings?.pickup_locations);
    const packages = listOf(settings?.packages);
    const setKind = (k) => {
        const base = { flat: { type: 'flat' }, carrier: { type: 'carrier', price: 0 }, pickup: { type: 'pickup', price: 0 }, local: { type: 'local' } }[k === 'fixed' ? 'flat' : k];
        setR((x) => ({ ...x, ...base, min_value: null, max_value: null, config: {} }));
    };
    const ok = () => {
        if (!r.name.trim()) { message.error(t('Name is required')); return; }
        if (r.type === 'pickup' && !r.config.location_id) { message.error(t('Choose a pickup location')); return; }
        if (r.type === 'local' && !listOf(r.config.postal_codes).length && !r.config.radius_km) { message.error(t('Local delivery needs postal codes or a radius')); return; }
        const out = { ...r, name: r.name.trim() };
        if (r.type === 'local' && !(r.config.radius_km && r.config.origin?.lat != null && r.config.origin?.lng != null)) {
            out.config = { postal_codes: listOf(r.config.postal_codes) };
        }
        onOk(out);
    };
    return (
        <Modal open={open} onCancel={onClose} title={rate?.name ? t('Edit rate') : t('Add rate')} destroyOnHidden onOk={ok} width={600}>
            <Form layout="vertical">
                <Form.Item label={t('Type')}>
                    <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)} optionType="button"
                        options={[{ value: 'fixed', label: t('Fixed rate') }, { value: 'carrier', label: t('Carrier rate') }, { value: 'pickup', label: t('Local pickup') }, { value: 'local', label: t('Local delivery') }]} />
                </Form.Item>
                <Row gutter={12}>
                    <Col span={12}><Form.Item label={t('Rate name')} required><Input value={r.name} placeholder={{ fixed: t('Standard'), carrier: 'UPS', pickup: t('Pick up in store'), local: t('Local delivery') }[kind]} onChange={(e) => set({ name: e.target.value })} /></Form.Item></Col>
                    <Col span={12}>
                        <Form.Item label={t('Delivery time (days)')}>
                            <Space.Compact style={{ width: '100%' }}>
                                <InputNumber min={0} max={365} placeholder={t('Min')} value={r.min_days} onChange={(v) => set({ min_days: v })} style={{ width: '50%' }} />
                                <InputNumber min={0} max={365} placeholder={t('Max')} value={r.max_days} onChange={(v) => set({ max_days: v })} style={{ width: '50%' }} />
                            </Space.Compact>
                        </Form.Item>
                    </Col>
                </Row>
                {kind === 'carrier' ? (
                    <>
                        <Form.Item label={<span>{t('Services')}<Hint title={t('Empty = every service the label provider returns')} /></span>}>
                            <Select mode="tags" value={listOf(r.config.services)} onChange={(services) => cfg({ services })} placeholder="usps_priority, ups_ground…" tokenSeparators={[',']} />
                        </Form.Item>
                        <Row gutter={12}>
                            <Col span={8}><Form.Item label={t('Markup %')}><InputNumber min={0} max={1000} value={(r.config.markup_bps || 0) / 100} onChange={(v) => cfg({ markup_bps: Math.round((v || 0) * 100) })} addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
                            <Col span={8}><Form.Item label={t('Markup amount')}><MoneyInput currency={currency} value={r.config.markup_flat || 0} onChange={(v) => cfg({ markup_flat: v || 0 })} /></Form.Item></Col>
                            <Col span={8}>
                                <Form.Item label={t('Package')}>
                                    <Select allowClear value={r.config.package_id || undefined} onChange={(package_id) => cfg({ package_id: package_id || null })} placeholder={t('Default')}
                                        options={packages.map((p) => ({ value: p.id, label: p.name }))} />
                                </Form.Item>
                            </Col>
                        </Row>
                        {!settings?.labels?.provider && <Text type="warning" style={{ fontSize: 12 }}>{t('Carrier rates need a label provider (below).')}</Text>}
                    </>
                ) : kind === 'pickup' ? (
                    <Row gutter={12}>
                        <Col span={14}>
                            <Form.Item label={t('Pickup location')} required>
                                <Select value={r.config.location_id || undefined} onChange={(location_id) => cfg({ location_id })} placeholder={pickups.length ? undefined : t('Add a pickup location first')}
                                    options={pickups.map((p) => ({ value: p.id, label: p.name }))} />
                            </Form.Item>
                        </Col>
                        <Col span={10}><Form.Item label={t('Price')}><MoneyInput currency={currency} value={r.price} onChange={(v) => set({ price: v || 0 })} /></Form.Item></Col>
                    </Row>
                ) : (
                    <>
                        <Form.Item label={t('Price')}><MoneyInput currency={currency} value={r.price} onChange={(v) => set({ price: v || 0 })} /></Form.Item>
                        {kind === 'fixed' && (
                            <>
                                <Form.Item label={t('Condition')}>
                                    <Radio.Group value={r.type} onChange={(e) => set({ type: e.target.value, min_value: null, max_value: null })}
                                        options={[{ value: 'flat', label: t('None') }, { value: 'weight', label: t('Order weight') }, { value: 'price', label: t('Order price') }]} />
                                </Form.Item>
                                {r.type !== 'flat' && (
                                    <Row gutter={12}>
                                        <Col span={12}><Form.Item label={t('Minimum')}>{r.type === 'weight'
                                            ? <InputNumber min={0} addonAfter={unit} value={w(r.min_value)} onChange={(v) => set({ min_value: g(v) })} style={{ width: '100%' }} />
                                            : <MoneyInput currency={currency} value={r.min_value} onChange={(v) => set({ min_value: v })} />}</Form.Item></Col>
                                        <Col span={12}><Form.Item label={t('Maximum')}>{r.type === 'weight'
                                            ? <InputNumber min={0} addonAfter={unit} value={w(r.max_value)} onChange={(v) => set({ max_value: g(v) })} style={{ width: '100%' }} />
                                            : <MoneyInput currency={currency} value={r.max_value} onChange={(v) => set({ max_value: v })} />}</Form.Item></Col>
                                    </Row>
                                )}
                            </>
                        )}
                        {kind === 'local' && (
                            <>
                                <Form.Item label={t('Postal codes')}>
                                    <Select mode="tags" value={listOf(r.config.postal_codes)} onChange={(postal_codes) => cfg({ postal_codes })} tokenSeparators={[',', ' ', '\n']} placeholder="10001, 10002, 941*" />
                                </Form.Item>
                                <Collapse ghost size="small" items={[{
                                    key: 'radius', label: t('Or by distance'),
                                    children: (
                                        <Row gutter={8}>
                                            <Col span={8}><Form.Item label={t('Radius')}><InputNumber min={0.1} max={500} addonAfter="km" value={r.config.radius_km} onChange={(radius_km) => cfg({ radius_km })} style={{ width: '100%' }} /></Form.Item></Col>
                                            <Col span={8}><Form.Item label={t('Latitude')}><InputNumber min={-90} max={90} step={0.0001} value={r.config.origin?.lat} onChange={(lat) => cfg({ origin: { ...(r.config.origin || {}), lat } })} style={{ width: '100%' }} /></Form.Item></Col>
                                            <Col span={8}><Form.Item label={t('Longitude')}><InputNumber min={-180} max={180} step={0.0001} value={r.config.origin?.lng} onChange={(lng) => cfg({ origin: { ...(r.config.origin || {}), lng } })} style={{ width: '100%' }} /></Form.Item></Col>
                                        </Row>
                                    ),
                                }]} />
                            </>
                        )}
                    </>
                )}
                <Collapse ghost size="small" items={[{
                    key: 'more', label: t('More'),
                    children: (
                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item label={<span>{t('Free over')}<Hint title={t('Free when the order subtotal reaches this amount')} /></span>}>
                                    <MoneyInput currency={currency} value={r.free_over} onChange={(v) => set({ free_over: v })} placeholder={t('Never')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}><Form.Item label={t('Delivery time text')}><Input value={r.eta} placeholder={t('3–5 days')} onChange={(e) => set({ eta: e.target.value })} /></Form.Item></Col>
                        </Row>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

const ZoneModal = ({ open, zone, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [z, setZ] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setZ({ name: '', countries: [], regions: {}, ...(zone || {}) }); }, [open, zone]);
    if (!z) return null;
    const save = async () => {
        if (!z.name.trim() || !z.countries.length) { message.error(t('Enter a name and at least one country')); return; }
        setBusy(true);
        try {
            const body = { name: z.name.trim(), countries: z.countries, regions: z.regions || {}, rates: listOf(z.rates) };
            if (zone?.id) await api.put(`/shipping-zones/${zone.id}`, body); else await api.post('/shipping-zones', body);
            onSaved();
        } catch (e) { toastErr(e, t('Save failed')); } finally { setBusy(false); }
    };
    const regionCountries = z.countries.filter((c) => c !== '*');
    return (
        <Modal open={open} onCancel={onClose} title={zone?.id ? t('Edit zone') : t('Create zone')} onOk={save} confirmLoading={busy} destroyOnHidden>
            <Form layout="vertical">
                <Form.Item label={t('Zone name')} required><Input value={z.name} placeholder={t('Domestic')} onChange={(e) => setZ({ ...z, name: e.target.value })} /></Form.Item>
                <Form.Item label={t('Countries')} required>
                    <CountrySelect allowRest mode="multiple" value={z.countries} onChange={(v) => setZ({ ...z, countries: v.includes('*') && !z.countries.includes('*') ? ['*'] : v.filter((c) => c !== '*' || v.length === 1) })} style={{ width: '100%' }} />
                </Form.Item>
                {regionCountries.length > 0 && regionCountries.length <= 5 && (
                    <Collapse ghost size="small" items={[{
                        key: 'regions', label: t('Only some regions'),
                        children: regionCountries.map((c) => (
                            <Form.Item key={c} label={countryLabel(c, t)} style={{ marginBottom: 8 }}>
                                <Select mode="tags" value={listOf(z.regions?.[c])} placeholder={t('All regions')} tokenSeparators={[',', ' ']}
                                    onChange={(v) => setZ({ ...z, regions: { ...(z.regions || {}), [c]: v.map((x) => String(x).toUpperCase()) } })} />
                            </Form.Item>
                        )),
                    }]} />
                )}
            </Form>
        </Modal>
    );
};

const Zones = ({ settings }) => {
    const { t } = useTranslation();
    const { api, currency, shop } = useSite();
    const unit = shop?.weight_unit || 'kg';
    const { data, loading, reload } = useLoad(() => api.get('/shipping-zones').catch((e) => (e.status === 404 ? [] : Promise.reject(e))), [api]);
    const [zoneModal, setZoneModal] = useState(null);
    const [rateModal, setRateModal] = useState(null); // {zone, index}
    const zones = listOf(data);
    const pickups = listOf(settings?.pickup_locations);

    const saveRates = async (zone, rates) => {
        try { await api.put(`/shipping-zones/${zone.id}`, { name: zone.name, countries: zone.countries, regions: zone.regions || {}, rates }); message.success(t('Saved')); reload(); } catch (e) { toastErr(e, t('Save failed')); }
    };
    const removeZone = async (z) => { try { await api.del(`/shipping-zones/${z.id}`); reload(); } catch (e) { toastErr(e); } };
    const cond = (r) => {
        if (r.type === 'carrier') return t('Calculated by carrier');
        if (r.type === 'pickup') return pickups.find((p) => p.id === r.config?.location_id)?.name || t('Local pickup');
        if (r.type === 'local') return r.config?.radius_km ? `≤ ${r.config.radius_km} km` : t('{{n}} postal codes', { n: listOf(r.config?.postal_codes).length });
        if (r.type === 'flat' || (r.min_value == null && r.max_value == null)) return '-';
        const f = (v) => (r.type === 'weight' ? `${+(v / UNIT[unit]).toFixed(2)} ${unit}` : money(v, currency));
        return [r.min_value != null && `≥ ${f(r.min_value)}`, r.max_value != null && `≤ ${f(r.max_value)}`].filter(Boolean).join(' · ');
    };
    const days = (r) => (r.min_days != null || r.max_days != null ? t('{{min}}–{{max}} days', { min: r.min_days ?? r.max_days, max: r.max_days ?? r.min_days }) : r.eta);
    const TYPE_TAG = { carrier: ['blue', 'Carrier rate'], pickup: ['green', 'Local pickup'], local: ['purple', 'Local delivery'] };

    if (loading && !zones.length) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text strong>{t('Shipping zones')}</Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setZoneModal({})}>{t('Create zone')}</Button>
            </div>
            {!zones.length && <Card><Empty description={t('No shipping zones. Customers can only buy items that don\'t need shipping.')}><Button type="primary" onClick={() => setZoneModal({})}>{t('Create zone')}</Button></Empty></Card>}
            {zones.map((z) => {
                const rates = listOf(z.rates);
                return (
                    <Card key={z.id} style={{ marginBottom: 16 }} size="small"
                        title={<Space wrap><Text strong>{z.name}</Text>{listOf(z.countries).slice(0, 8).map((c) => <Tag key={c}>{c !== '*' && <CountryFlag code={c} />} {countryLabel(c, t)}{listOf(z.regions?.[c]).length ? ` (${listOf(z.regions[c]).length})` : ''}</Tag>)}{listOf(z.countries).length > 8 && <Tag>+{listOf(z.countries).length - 8}</Tag>}</Space>}
                        extra={(
                            <Space>
                                <Button size="small" icon={<EditOutlined />} onClick={() => setZoneModal(z)} />
                                <Popconfirm title={t('Delete this zone?')} onConfirm={() => removeZone(z)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                            </Space>
                        )}>
                        <Table size="small" rowKey={(r, i) => r.id || i} dataSource={rates} pagination={false} scroll={{ x: 600 }}
                            locale={{ emptyText: t('No rates') }}
                            columns={[
                                { title: t('Rate', { context: 'shipping' }), key: 'n', render: (_, r) => <div><Space size={4}>{r.name}{TYPE_TAG[r.type] && <Tag color={TYPE_TAG[r.type][0]} style={{ marginInlineEnd: 0 }}>{t(TYPE_TAG[r.type][1])}</Tag>}</Space>{days(r) && <div><Text type="secondary" style={{ fontSize: 12 }}>{days(r)}</Text></div>}</div> },
                                { title: t('Condition'), key: 'c', render: (_, r) => cond(r) },
                                { title: t('Price'), key: 'p', render: (_, r) => (r.type === 'carrier' ? t('Live rate') : r.price ? money(r.price, currency) : t('Free')) },
                                { title: t('Free over'), key: 'f', render: (_, r) => (r.free_over != null ? money(r.free_over, currency) : '-') },
                                {
                                    key: 'x', width: 90, render: (_, r, i) => (
                                        <Space size={0}>
                                            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setRateModal({ zone: z, index: i })} />
                                            <Popconfirm title={t('Delete this rate?')} onConfirm={() => saveRates(z, rates.filter((_, j) => j !== i))}><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                                        </Space>
                                    ),
                                },
                            ]} />
                        <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => setRateModal({ zone: z, index: -1 })}>{t('Add rate')}</Button>
                    </Card>
                );
            })}
            <ZoneModal open={!!zoneModal} zone={zoneModal?.id ? zoneModal : null} onClose={() => setZoneModal(null)} onSaved={() => { setZoneModal(null); reload(); }} />
            <RateModal open={!!rateModal} settings={settings} rate={rateModal && rateModal.index >= 0 ? listOf(rateModal.zone.rates)[rateModal.index] : null}
                onClose={() => setRateModal(null)}
                onOk={(r) => {
                    const rates = [...listOf(rateModal.zone.rates)];
                    if (rateModal.index >= 0) rates[rateModal.index] = r; else rates.push(r);
                    saveRates(rateModal.zone, rates);
                    setRateModal(null);
                }} />
        </div>
    );
};

// ---------------------------------------------------------------- address / pickup locations

const AddressFields = ({ value, onChange, withName = true }) => {
    const { t } = useTranslation();
    const a = value || {};
    const set = (p) => onChange({ ...a, ...p });
    const f = (k, label, span = 12, ph) => (
        <Col xs={24} sm={span}><Form.Item label={label} style={{ marginBottom: 8 }}><Input value={a[k] || ''} placeholder={ph} onChange={(e) => set({ [k]: e.target.value })} /></Form.Item></Col>
    );
    return (
        <Row gutter={12}>
            {withName && f('name', t('Name'))}
            {withName && f('company', t('Company'))}
            {f('line1', t('Address'), 24)}
            {f('line2', t('Apartment, suite, etc.'), 24)}
            {f('city', t('City'), 8)}
            {f('region', t('State / province'), 8)}
            {f('postal_code', t('Postal code'), 8)}
            <Col xs={24} sm={12}><Form.Item label={t('Country')} style={{ marginBottom: 8 }}><CountrySelect value={a.country || undefined} onChange={(country) => set({ country })} style={{ width: '100%' }} /></Form.Item></Col>
            {f('phone', t('Phone'))}
        </Row>
    );
};

const PickupModal = ({ open, loc, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [p, setP] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setP({ name: '', address: {}, hours: {}, instructions: '', ready_in_hours: 24, active: true, ...(loc || {}) }); }, [open, loc]);
    if (!p) return null;
    const save = async () => {
        setBusy(true);
        try {
            const body = { name: p.name, address: p.address, hours: p.hours, instructions: p.instructions, ready_in_hours: p.ready_in_hours, active: p.active };
            if (loc?.id) await api.put(`/pickup-locations/${loc.id}`, body); else await api.post('/pickup-locations', body);
            onSaved();
        } catch (e) { toastErr(e, t('Save failed')); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} onOk={save} confirmLoading={busy} title={loc?.id ? t('Edit pickup location') : t('Add pickup location')} width={640} destroyOnHidden>
            <Form layout="vertical">
                <Row gutter={12}>
                    <Col span={14}><Form.Item label={t('Name')} required style={{ marginBottom: 8 }}><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder={t('Main store')} /></Form.Item></Col>
                    <Col span={10}><Form.Item label={t('Ready in')} style={{ marginBottom: 8 }}><InputNumber min={0} max={720} addonAfter={t('hours')} value={p.ready_in_hours} onChange={(v) => setP({ ...p, ready_in_hours: v ?? 24 })} style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
                <AddressFields value={p.address} withName={false} onChange={(address) => setP({ ...p, address })} />
                <Collapse ghost size="small" items={[{
                    key: 'hours', label: t('Opening hours'),
                    children: (
                        <Row gutter={8}>
                            {DAYS.map((d) => (
                                <Col span={12} key={d}>
                                    <Input addonBefore={t(DAY_LABELS[d])} placeholder="09:00-18:00" value={p.hours?.[d] || ''} style={{ marginBottom: 6 }}
                                        onChange={(e) => setP({ ...p, hours: { ...(p.hours || {}), [d]: e.target.value.trim() } })} />
                                </Col>
                            ))}
                        </Row>
                    ),
                }]} />
                <Form.Item label={t('Pickup instructions')} style={{ marginBottom: 8 }}><Input.TextArea value={p.instructions} autoSize={{ minRows: 2 }} onChange={(e) => setP({ ...p, instructions: e.target.value })} /></Form.Item>
                <Checkbox checked={p.active} onChange={(e) => setP({ ...p, active: e.target.checked })}>{t('Active')}</Checkbox>
            </Form>
        </Modal>
    );
};

const PickupLocations = ({ list, onChanged }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [edit, setEdit] = useState(undefined);
    const del = async (p) => { try { await api.del(`/pickup-locations/${p.id}`); onChanged(); } catch (e) { toastErr(e); } };
    return (
        <>
            {list.map((p) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <EnvironmentOutlined />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong>{p.name}</Text> {!p.active && <Tag>{t('Inactive')}</Tag>}
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{[p.address?.line1, p.address?.city, p.address?.country].filter(Boolean).join(', ')}</Text></div>
                    </div>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEdit(p)} />
                    <Popconfirm title={t('Delete?')} onConfirm={() => del(p)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                </div>
            ))}
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setEdit(null)} style={{ marginTop: 4 }}>{t('Add pickup location')}</Button>
            <PickupModal open={edit !== undefined} loc={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); onChanged(); }} />
        </>
    );
};

// ---------------------------------------------------------------- settings panels

const Packages = ({ value, onChange }) => {
    const { t } = useTranslation();
    const list = listOf(value);
    const set = (i, p) => onChange(list.map((x, j) => (j === i ? { ...x, ...p } : (p.default ? { ...x, default: false } : x))));
    const num = (i, k, suffix) => <InputNumber size="small" min={k === 'weight_g' ? 0 : 1} max={k === 'weight_g' ? 100000 : 5000} value={list[i][k]} addonAfter={suffix} onChange={(v) => set(i, { [k]: v || 0 })} style={{ width: '100%' }} />;
    return (
        <>
            <Table size="small" rowKey={(_, i) => i} pagination={false} dataSource={list} scroll={{ x: 640 }} locale={{ emptyText: t('No packages') }} columns={[
                { title: t('Name'), key: 'n', render: (_, p, i) => <Input size="small" value={p.name} onChange={(e) => set(i, { name: e.target.value })} /> },
                { title: t('Length'), key: 'l', width: 110, render: (_, p, i) => num(i, 'length_mm', 'mm') },
                { title: t('Width'), key: 'w', width: 110, render: (_, p, i) => num(i, 'width_mm', 'mm') },
                { title: t('Height'), key: 'h', width: 110, render: (_, p, i) => num(i, 'height_mm', 'mm') },
                { title: t('Empty weight'), key: 'g', width: 110, render: (_, p, i) => num(i, 'weight_g', 'g') },
                { title: t('Default'), key: 'd', width: 70, align: 'center', render: (_, p, i) => <Radio checked={!!p.default} onChange={() => set(i, { default: true })} /> },
                { key: 'x', width: 40, render: (_, p, i) => <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(list.filter((_, j) => j !== i))} /> },
            ]} />
            <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8 }}
                onClick={() => onChange([...list, { name: '', length_mm: 300, width_mm: 200, height_mm: 100, weight_g: 100, default: !list.length }])}>{t('Add package')}</Button>
        </>
    );
};

const KeyInput = ({ value, onChange }) => {
    const { t } = useTranslation();
    return <Input.Password value={value} onChange={(e) => onChange(e.target.value)} placeholder={t('API key')} autoComplete="new-password" visibilityToggle={false} />;
};

const DeliverySettings = ({ settings, onSaved }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(null);
    useEffect(() => {
        if (settings) setS({ ship_from: settings.ship_from || {}, packages: listOf(settings.packages), tracking: { ...(settings.tracking || {}) }, labels: { ...(settings.labels || {}) }, returns: { enabled: true, window_days: 30, ...(settings.returns || {}) } });
    }, [settings]);
    if (!s) return null;
    const save = async (part, body) => {
        setBusy(part);
        try { const d = await api.put('/shipping-settings', body); message.success(t('Saved')); onSaved(d); } catch (e) { toastErr(e, t('Save failed')); } finally { setBusy(null); }
    };
    const saveBtn = (part, body) => <Button type="primary" size="small" loading={busy === part} onClick={() => save(part, body)} style={{ marginTop: 8 }}>{t('Save')}</Button>;
    const providerSelect = (k, options) => (
        <Select allowClear style={{ width: 200 }} value={s[k].provider || undefined} placeholder={t('None')} onChange={(provider) => setS({ ...s, [k]: { ...s[k], provider: provider || null } })} options={options} />
    );
    const keyBody = (k) => ({ provider: s[k].provider || null, api_key: s[k].api_key, ...(k === 'tracking' ? { notify_out_for_delivery: s.tracking.notify_out_for_delivery, notify_delivered: s.tracking.notify_delivered } : {}) });
    return (
        <Collapse items={[
            {
                key: 'from', label: t('Ship from'),
                children: <>
                    <AddressFields value={s.ship_from} onChange={(ship_from) => setS({ ...s, ship_from })} />
                    {saveBtn('from', { ship_from: s.ship_from })}
                </>,
            },
            {
                key: 'packages', label: t('Packages'),
                children: <>
                    <Packages value={s.packages} onChange={(packages) => setS({ ...s, packages })} />
                    <div>{saveBtn('packages', { packages: s.packages.map((p) => ({ ...p, name: String(p.name || '').trim() })) })}</div>
                </>,
            },
            {
                key: 'tracking', label: <Space>{t('Tracking')}{s.tracking.provider && <Tag color="green">{s.tracking.provider === '17track' ? '17TRACK' : 'AfterShip'}</Tag>}</Space>,
                children: <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        {providerSelect('tracking', [{ value: '17track', label: '17TRACK' }, { value: 'aftership', label: 'AfterShip' }])}
                        {s.tracking.provider && <div style={{ width: 300 }}><KeyInput value={s.tracking.api_key} onChange={(api_key) => setS({ ...s, tracking: { ...s.tracking, api_key } })} /></div>}
                    </Space>
                    <Space wrap>
                        <Checkbox checked={!!s.tracking.notify_out_for_delivery} onChange={(e) => setS({ ...s, tracking: { ...s.tracking, notify_out_for_delivery: e.target.checked } })}>{t('Email customers when out for delivery')}</Checkbox>
                        <Checkbox checked={!!s.tracking.notify_delivered} onChange={(e) => setS({ ...s, tracking: { ...s.tracking, notify_delivered: e.target.checked } })}>{t('Email customers when delivered')}</Checkbox>
                    </Space>
                    {saveBtn('tracking', { tracking: keyBody('tracking') })}
                </Space>,
            },
            {
                key: 'labels', label: <Space>{t('Shipping labels')}{s.labels.provider && <Tag color="green">{s.labels.provider === 'shippo' ? 'Shippo' : 'EasyPost'}</Tag>}{s.labels.test_mode && <Tag color="orange">{t('Test')}</Tag>}</Space>,
                children: <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        {providerSelect('labels', [{ value: 'shippo', label: 'Shippo' }, { value: 'easypost', label: 'EasyPost' }])}
                        {s.labels.provider && <div style={{ width: 300 }}><KeyInput value={s.labels.api_key} onChange={(api_key) => setS({ ...s, labels: { ...s.labels, api_key } })} /></div>}
                    </Space>
                    {saveBtn('labels', { labels: keyBody('labels') })}
                </Space>,
            },
            {
                key: 'returns', label: t('Returns'),
                children: <Space wrap align="center">
                    <Switch checked={!!s.returns.enabled} onChange={(enabled) => setS({ ...s, returns: { ...s.returns, enabled } })} />
                    <Text>{t('Customers can request returns')}</Text>
                    {s.returns.enabled && <InputNumber min={0} max={365} value={s.returns.window_days} addonAfter={t('days')} onChange={(v) => setS({ ...s, returns: { ...s.returns, window_days: v ?? 30 } })} style={{ width: 140 }} />}
                    {saveBtn('returns', { returns: s.returns })}
                </Space>,
            },
        ]} />
    );
};

const Shipping = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [settings, setSettings] = useState(null);
    const [soon, setSoon] = useState(false);
    const loadSettings = () => api.get('/shipping-settings', null, QUIET).then(setSettings).catch((e) => { if (e?.status === 404) setSoon(true); else toastErr(e); setSettings({}); });
    useEffect(() => { loadSettings(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
    const reloadPickups = () => api.get('/pickup-locations', null, QUIET).then((d) => setSettings((s) => ({ ...s, pickup_locations: listOf(d) }))).catch(() => {});

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Zones settings={settings} />
            {!soon && settings && (
                <>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('Pickup locations')}</Text>
                        <PickupLocations list={listOf(settings.pickup_locations)} onChanged={reloadPickups} />
                    </div>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('Delivery settings')}</Text>
                        <DeliverySettings settings={settings} onSaved={setSettings} />
                    </div>
                </>
            )}
        </Space>
    );
};

export default Shipping;
